import flatten from "lodash/flatten";
import uniq from "lodash/uniq";
import { useMemo } from "react";
import { WSInfiniteQueryConfig } from "@ws-react-query";
import { PayeeDisplayInfo } from "../../modules/Payables/components/TablePayables/columns";
import { getPayeeNames } from "../../modules/Payees/selectors/getPayeeNames";
import {
  listPayables,
  ListPayablesResponse,
  Payable
} from "../../services/api/payments/payables";
import { IPayeeRow } from "../../services/search";
import { useXWingspanExpansion } from "../../shared/hooks/useXWingspanExpansion";
import { WSServiceError } from "../../utils/serviceHelper";
import { useWSInfiniteQuery } from "../helpers";
import { fetchAllPayeeRowsByIds } from "../search/payee/queries/useQueryAllPayeeRowsByIds";
import { QUERY_PAYABLES_WITH_PAYEES } from "./keys";
import { UseQueryPayablesRequest } from "./useQueryPayables";

const PAGE_SIZE = 20;

type PayablesPageParam = { token: string; resultSetId: string };

type PayablesWithPayeesPage = ListPayablesResponse & { payees: IPayeeRow[] };

/**
 * Lists payables and resolves the payees on each page within the same query, so
 * a page never settles with payables visible but contractor names still missing
 * — the new-table equivalent of the legacy `usePayablesQuery({ joinPayee: true })`
 * join, on the first page and every infinite-scroll page alike.
 *
 * Payees are resolved through the per-id cache (`fetchAllPayeeRowsByIds`), so
 * only payees missing from the cache are requested.
 *
 * Returns the payables query shaped like `useQueryPayables` (`data`,
 * `resultSetId`, `fetchNextPage`, ...) plus a `payeesMap` keyed by `payeeId`.
 */
export const useQueryPayablesWithPayees = (
  request: UseQueryPayablesRequest,
  config?: WSInfiniteQueryConfig<PayablesWithPayeesPage, WSServiceError>
) => {
  const xWingspanExpansion = useXWingspanExpansion();

  const query = useWSInfiniteQuery<PayablesWithPayeesPage, WSServiceError>(
    [QUERY_PAYABLES_WITH_PAYEES, request, xWingspanExpansion],
    async ({ pageParam }) => {
      const cursor = pageParam as PayablesPageParam | undefined;
      const payables = await listPayables({
        ...request,
        "page[mode]": "materialized",
        "page[size]": PAGE_SIZE,
        ...(cursor?.resultSetId
          ? { "page[resultSetId]": cursor.resultSetId }
          : {}),
        ...(cursor?.token ? { "page[token]": cursor.token } : {})
      });

      const payeeIds = uniq(
        payables.items
          .map(payable => payable.payeeId)
          .filter((id): id is string => Boolean(id))
      );

      const payees =
        payeeIds.length > 0
          ? await fetchAllPayeeRowsByIds(payeeIds, xWingspanExpansion)
          : [];

      return { ...payables, payees };
    },
    {
      getNextPageParam: (lastPage, allPages): PayablesPageParam | undefined => {
        if (!lastPage.nextPageToken) return undefined;
        const resultSetId =
          lastPage.resultSetId ?? allPages[0]?.resultSetId ?? "";
        if (!resultSetId) return undefined;
        return { token: lastPage.nextPageToken, resultSetId };
      },
      ...config
    }
  );

  const items: Payable[] | undefined = query.data
    ? flatten(query.data.pages.map(p => p.items))
    : undefined;

  const resultSetId = query.data?.pages[0]?.resultSetId;

  const payeesMap = useMemo<Map<string, PayeeDisplayInfo>>(() => {
    const map = new Map<string, PayeeDisplayInfo>();
    query.data?.pages.forEach(page => {
      page.payees.forEach(row => {
        if (!row.user) {
          return;
        }
        const names = getPayeeNames(row);
        map.set(row.payeeId, {
          name: names.getResolvedName(),
          companyName: names.companyName,
          email: row.user.email
        });
      });
    });
    return map;
  }, [query.data]);

  return {
    ...query,
    data: items,
    resultSetId,
    payeesMap
  };
};
